import { useEffect, useState } from "react";
import type { AxiosError } from "axios";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import AnnouncementForm, {
	AnnouncementFormValues
} from "../../components/AnnouncementForm/AnnouncementForm";
import AnnouncementAnalytics from "../../components/AnnouncementAnalytics/AnnouncementAnalytics";
import useAnnouncement from "../../hooks/useAnnouncement";
import useEditAnnouncement from "../../hooks/useEditAnnouncement";
import useDeleteAnnouncement from "../../hooks/useDeleteAnnouncement";
import useSendAnnouncement from "../../hooks/useSendAnnouncement";
import { adaptEditAnnouncementPayload } from "../../adapters/adaptAnnouncementPayload";
import { formValuesToAnnouncement } from "../../adapters/formValuesToAnnouncement";
import useAnnouncementAnalytics from "../../hooks/useAnnouncementAnalytics";
import { safeImageUrl } from "src/utils/safeImageUrl";
import {
	announcementToFormValues,
	formatAudience
} from "../../utils/announcementFormHelpers";

function formatDate(iso: string | null): string {
	if (!iso) return "\u2014";
	const date = new Date(iso);
	if (!Number.isFinite(date.getTime())) return "\u2014";
	return date.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	});
}

const EditAnnouncement = () => {
	const navigate = useNavigate();
	const { announcementId } = useParams<{ announcementId: string }>();
	const id = Number(announcementId);
	const isValidId = Number.isInteger(id) && id > 0;

	const {
		data: announcement,
		isLoading,
		error: announcementError
	} = useAnnouncement(isValidId ? id : 0);

	const { mutate: editAnnouncement, isLoading: isEditing } =
		useEditAnnouncement();
	const { mutate: deleteAnnouncement, isLoading: isDeleting } =
		useDeleteAnnouncement();
	const { mutate: sendAnnouncement, isLoading: isSending } =
		useSendAnnouncement();

	const isEditable =
		announcement?.status === "draft" || announcement?.status === "scheduled";
	const { data: analytics = null } = useAnnouncementAnalytics(
		id,
		isValidId && !isEditable && !!announcement
	);

	const [formDefaults, setFormDefaults] =
		useState<AnnouncementFormValues | null>(null);
	const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);

	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showSendConfirm, setShowSendConfirm] = useState(false);
	const [pendingData, setPendingData] = useState<{
		data: AnnouncementFormValues;
		imageRemoved: boolean;
	} | null>(null);

	useEffect(() => {
		if (announcement) {
			setFormDefaults(announcementToFormValues(announcement));
			setExistingImageUrl(safeImageUrl(announcement.heroImage));
		}
	}, [announcement]);

	if (!isValidId) {
		return <Navigate to="/announcements" replace />;
	}

	if (
		!isLoading &&
		announcementError &&
		(announcementError as AxiosError)?.response?.status === 404
	) {
		return <Navigate to="/announcements" replace />;
	}

	if (isLoading || !announcement) {
		return (
			<Page>
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<p className="text-[13px] text-muted-foreground">Loading...</p>
				</div>
			</Page>
		);
	}

	// Editable mode handlers
	const handleSaveDraft = (
		data: AnnouncementFormValues,
		imageRemoved: boolean
	) => {
		const announcementData = formValuesToAnnouncement(data, id);
		const payload = adaptEditAnnouncementPayload(
			announcementData,
			data.heroImage,
			imageRemoved,
			data.blockFiles
		);
		editAnnouncement(
			{ id, payload },
			{
				onSuccess: () => navigate("/announcements")
			}
		);
	};

	const handleSendOrSchedule = (
		data: AnnouncementFormValues,
		imageRemoved: boolean
	) => {
		setPendingData({ data, imageRemoved });
		setShowSendConfirm(true);
	};

	const confirmSend = () => {
		if (!pendingData) return;
		const { data, imageRemoved } = pendingData;
		const announcementData = formValuesToAnnouncement(data, id);
		const payload = adaptEditAnnouncementPayload(
			announcementData,
			data.heroImage,
			imageRemoved,
			data.blockFiles
		);
		editAnnouncement(
			{ id, payload },
			{
				onSuccess: () => {
					sendAnnouncement(id, {
						onSuccess: () => {
							setShowSendConfirm(false);
							setPendingData(null);
							navigate("/announcements");
						},
						onError: () => {
							setShowSendConfirm(false);
							setPendingData(null);
						}
					});
				},
				onError: () => {
					setShowSendConfirm(false);
					setPendingData(null);
				}
			}
		);
	};

	const confirmDelete = () => {
		deleteAnnouncement(id, {
			onSuccess: () => {
				setShowDeleteConfirm(false);
				navigate("/announcements");
			}
		});
	};

	const getSendConfirmMessage = () => {
		if (!pendingData) return "";
		const { data } = pendingData;
		const audience =
			data.audienceType === "everyone"
				? "all users"
				: data.audienceType === "platform"
					? `${data.audiencePlatform === "both" ? "all" : data.audiencePlatform} users`
					: `${data.audienceUserIds.length} selected user${data.audienceUserIds.length !== 1 ? "s" : ""}`;

		if (data.scheduleType === "scheduled") {
			return `Schedule this announcement for ${data.scheduledDate} at ${data.scheduledTime || "00:00"} to ${audience}?`;
		}
		return `Send this announcement now to ${audience}?`;
	};

	// Editable view
	if (isEditable) {
		return (
			<Page>
				{formDefaults && (
					<AnnouncementForm
						defaultValues={formDefaults}
						existingImageUrl={existingImageUrl}
						onSubmit={handleSaveDraft}
						onSendOrSchedule={handleSendOrSchedule}
						onDelete={() => setShowDeleteConfirm(true)}
						isSubmitting={isEditing}
						isSending={isSending}
						isDeleting={isDeleting}
					/>
				)}

				<ConfirmationModal
					isOpen={showSendConfirm}
					title={
						pendingData?.data.scheduleType === "scheduled"
							? "Schedule announcement?"
							: "Send announcement?"
					}
					description={getSendConfirmMessage()}
					onCancel={() => {
						if (!isEditing && !isSending) {
							setShowSendConfirm(false);
							setPendingData(null);
						}
					}}
					cancelBtnProps={{ disabled: isEditing || isSending }}
					onSubmit={confirmSend}
					submitBtnProps={{ isLoading: isEditing || isSending }}
				/>

				<ConfirmationModal
					isOpen={showDeleteConfirm}
					title="Delete announcement?"
					description="This action cannot be undone."
					onCancel={() => {
						if (!isDeleting) setShowDeleteConfirm(false);
					}}
					cancelBtnProps={{ disabled: isDeleting }}
					onSubmit={confirmDelete}
					submitBtnProps={{ isLoading: isDeleting }}
				/>
			</Page>
		);
	}

	// Read-only view (sent / expired)
	const statusBadge =
		announcement.status === "sent" ? (
			<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700">
				Sent
			</span>
		) : (
			<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-50 text-red-700">
				Expired
			</span>
		);

	const sentDate = announcement.sentAt
		? new Date(announcement.sentAt).toLocaleDateString("en-GB", {
				day: "numeric",
				month: "long",
				year: "numeric"
			})
		: null;

	const authorName = announcement.createdBy
		? `${announcement.createdBy.firstName} ${announcement.createdBy.lastName}`
		: null;

	const authorInitials = authorName
		? authorName
				.split(" ")
				.slice(0, 2)
				.map((n) => n[0])
				.join("")
				.toUpperCase()
		: "";

	const sanitisedBody = DOMPurify.sanitize(announcement.body ?? "", {
		ALLOWED_TAGS: [
			"p",
			"br",
			"strong",
			"em",
			"u",
			"a",
			"ul",
			"ol",
			"li",
			"h1",
			"h2",
			"h3",
			"blockquote",
			"span",
			"img",
			"video",
			"source"
		],
		ALLOWED_ATTR: [
			"href",
			"target",
			"rel",
			"src",
			"alt",
			"class",
			"controls",
			"type",
			"aria-label",
			"data-block-index"
		]
	});

	return (
		<Page>
			<div className="space-y-4">
				{/* Status bar */}
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<div className="flex items-start justify-between gap-4">
						<div className="min-w-0 flex-1">
							<label className="font-heading text-lg font-medium text-foreground block mb-1">
								{announcement.title}
							</label>
							<p className="text-[13px] text-muted-foreground">
								This announcement has been {announcement.status} and cannot be
								edited.
							</p>
						</div>
						{statusBadge}
					</div>
				</div>

				{/* Content preview (mobile width) */}
				<div className="rounded-xl border border-border bg-white shadow-sm max-w-[390px] p-5">
					{/* Date */}
					{sentDate && (
						<div className="flex items-center gap-2 mb-2">
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								className="text-zinc-400"
								aria-hidden="true"
								focusable="false"
							>
								<rect x="3" y="4" width="18" height="18" rx="2" />
								<line x1="16" y1="2" x2="16" y2="6" />
								<line x1="8" y1="2" x2="8" y2="6" />
								<line x1="3" y1="10" x2="21" y2="10" />
							</svg>
							<span className="text-[13px] text-zinc-400">{sentDate}</span>
						</div>
					)}

					{/* Title */}
					<h2 className="text-xl font-bold text-foreground mb-3">
						{announcement.title}
					</h2>

					{/* Author */}
					{authorName && (
						<div className="flex items-center gap-2.5 mb-4">
							<div className="w-8 h-8 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center text-xs font-medium">
								{authorInitials}
							</div>
							<span className="text-sm font-medium text-foreground">
								{authorName}
							</span>
						</div>
					)}

					{/* Hero image */}
					{safeImageUrl(announcement.heroImage) && (
						<div className="rounded-lg overflow-hidden mb-4 -mx-1">
							<img
								src={safeImageUrl(announcement.heroImage) as string}
								alt=""
								className="w-full object-cover"
							/>
						</div>
					)}

					{/* Body content */}
					{sanitisedBody && (
						<div
							className="prose prose-sm max-w-none text-foreground"
							dangerouslySetInnerHTML={{ __html: sanitisedBody }}
						/>
					)}
				</div>

				{/* Metadata grid */}
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<label className="font-heading text-lg font-medium text-foreground mb-4 block">
						Details
					</label>
					<dl className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4">
						<div>
							<dt className="text-sm font-medium text-zinc-600">Sent</dt>
							<dd className="mt-1 text-sm text-foreground">
								{formatDate(announcement.sentAt)}
							</dd>
						</div>
						<div>
							<dt className="text-sm font-medium text-zinc-600">Audience</dt>
							<dd className="mt-1 text-sm text-foreground">
								{formatAudience(announcement)}
							</dd>
						</div>
						<div>
							<dt className="text-sm font-medium text-zinc-600">Expiry</dt>
							<dd className="mt-1 text-sm text-foreground">
								{formatDate(announcement.expiresAt)}
							</dd>
						</div>
						<div>
							<dt className="text-sm font-medium text-zinc-600">Created by</dt>
							<dd className="mt-1 text-sm text-foreground">
								{announcement.createdBy
									? `${announcement.createdBy.firstName} ${announcement.createdBy.lastName}`
									: "\u2014"}
							</dd>
						</div>
					</dl>
				</div>

				{/* Analytics */}
				{analytics && <AnnouncementAnalytics analytics={analytics} />}

				{/* Back link */}
				<div className="pt-2">
					<button
						type="button"
						onClick={() => navigate("/announcements")}
						className="flex items-center gap-2 text-sm text-zinc-500 hover:text-foreground transition-colors"
					>
						<ArrowLeft size={16} />
						Back to Announcements
					</button>
				</div>
			</div>
		</Page>
	);
};

export default EditAnnouncement;
